import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Star, Check, X, Trash2, Plus, BadgeCheck, ExternalLink, Upload } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ReviewCsvImport from '@/components/admin/ReviewCsvImport';

const STATUS_STYLES = {
  pending: 'bg-yellow-400/10 text-yellow-400',
  approved: 'bg-green-400/10 text-green-400',
  rejected: 'bg-red-400/10 text-red-400',
};

export default function ReviewManager() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    author_name: '', author_location: '', quote: '', rating: 5,
    source: 'google', source_url: '', source_label: 'Google', verified: false,
  });

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['admin-reviews'],
    queryFn: () => base44.entities.Review.list('-created_date'),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });

  const updateStatus = async (id, status) => {
    await base44.entities.Review.update(id, { status });
    refresh();
  };

  const toggleVerified = async (rev) => {
    await base44.entities.Review.update(rev.id, { verified: !rev.verified });
    refresh();
  };

  const remove = async (id) => {
    if (!confirm('Delete this review permanently?')) return;
    await base44.entities.Review.delete(id);
    refresh();
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.author_name || !form.quote) return;
    setSaving(true);
    try {
      await base44.entities.Review.create({
        ...form,
        rating: Number(form.rating) || 5,
        status: 'approved',
        source_label: form.source_label || (form.source === 'google' ? 'Google' : form.source === 'trustpilot' ? 'Trustpilot' : 'Customer Review'),
      });
      setForm({ author_name: '', author_location: '', quote: '', rating: 5, source: 'google', source_url: '', source_label: 'Google', verified: false });
      setShowForm(false);
      refresh();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-white/30 text-xs">Only approved reviews appear on the storefront. {reviews.filter(r => r.status === 'pending').length} pending.</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowImport((s) => !s); setShowForm(false); }}
            className="flex items-center gap-2 bg-primary/10 border border-primary/40 hover:bg-primary hover:border-primary text-white text-xs font-bold tracking-wider px-4 py-2.5 transition-colors"
          >
            <Upload className="w-4 h-4" /> BULK CSV IMPORT
          </button>
          <button
            onClick={() => { setShowForm((s) => !s); setShowImport(false); }}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-wider px-4 py-2.5 transition-colors"
          >
            <Plus className="w-4 h-4" /> ADD REVIEW
          </button>
        </div>
      </div>

      {showImport && <ReviewCsvImport onClose={() => setShowImport(false)} />}

      {showForm && (
        <form onSubmit={handleAdd} className="bg-[#111115] border border-white/5 p-6 mb-6 space-y-4">
          <p className="text-primary text-[10px] font-bold tracking-widest">IMPORT A REAL REVIEW (GOOGLE / TRUSTPILOT)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">NAME *</Label>
              <Input value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className="bg-[#0d0d0f] border-white/10 text-white" required />
            </div>
            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">LOCATION</Label>
              <Input value={form.author_location} onChange={(e) => setForm({ ...form, author_location: e.target.value })} className="bg-[#0d0d0f] border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">SOURCE</Label>
              <select
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value, source_label: e.target.value === 'google' ? 'Google' : e.target.value === 'trustpilot' ? 'Trustpilot' : 'Customer Review' })}
                className="w-full h-9 bg-[#0d0d0f] border border-white/10 text-white text-sm px-2"
              >
                <option value="google">Google</option>
                <option value="trustpilot">Trustpilot</option>
                <option value="customer">Customer</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">REVIEW TEXT *</Label>
            <Textarea value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={3} className="bg-[#0d0d0f] border-white/10 text-white resize-none" required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">SOURCE LINK</Label>
              <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://..." className="bg-[#0d0d0f] border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">RATING</Label>
              <select
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                className="w-full h-9 bg-[#0d0d0f] border border-white/10 text-white text-sm px-2"
              >
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} Stars</option>)}
              </select>
            </div>
          </div>
          <button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-3 transition-colors disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} PUBLISH REVIEW
          </button>
        </form>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 border border-white/5 bg-[#111115]">
          <p className="text-white/30 text-sm">No reviews yet. Add real reviews or approve customer submissions.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="bg-[#111115] border border-white/5 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-white font-bold text-sm">{r.author_name}</p>
                    {r.author_location && <span className="text-white/30 text-xs">· {r.author_location}</span>}
                    {r.verified && (
                      <span className="inline-flex items-center gap-1 text-primary text-[10px] font-bold tracking-widest">
                        <BadgeCheck className="w-3.5 h-3.5" /> VERIFIED
                      </span>
                    )}
                    <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 ${STATUS_STYLES[r.status]}`}>
                      {r.status.toUpperCase()}
                    </span>
                    <span className="text-white/20 text-[10px] uppercase tracking-wider">{r.source}</span>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className={`w-3 h-3 ${j < (r.rating || 5) ? 'text-primary fill-primary' : 'text-white/10'}`} />
                    ))}
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed">"{r.quote}"</p>
                  {r.source_url && (
                    <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-primary text-xs font-bold mt-2 hover:text-primary/80">
                      VIEW ORIGINAL <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  {r.status !== 'approved' && (
                    <button onClick={() => updateStatus(r.id, 'approved')} title="Approve" className="border border-green-500/30 text-green-400 hover:bg-green-500/10 p-2 transition-colors">
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  {r.status !== 'rejected' && (
                    <button onClick={() => updateStatus(r.id, 'rejected')} title="Reject" className="border border-white/10 text-white/40 hover:bg-white/5 p-2 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => toggleVerified(r)} title="Toggle verified" className={`border p-2 transition-colors ${r.verified ? 'border-primary/40 text-primary' : 'border-white/10 text-white/40 hover:bg-white/5'}`}>
                    <BadgeCheck className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(r.id)} title="Delete" className="border border-red-500/30 text-red-400 hover:bg-red-500/10 p-2 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}